import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, BookOpen, FileText, CircleCheck, Settings, ArrowRight, MessageSquare, MoreHorizontal, Clock } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { useBook } from "@/context/BookContext";
import { Page, Header, Card, Badge, Progress, Img } from "@/components/common";

const Stat = ({ icon: I, label, value, sub, tone = "teal", testid }) => (
  <div className="card-z p-5 flex gap-4 items-start" data-testid={testid}>
    <div className={`w-12 h-12 rounded-full grid place-items-center ${tone === "red" ? "bg-[#fdeceb] text-[#d1453b]" : "bg-[#e3f1f5] text-teal"}`}><I size={22} strokeWidth={1.6} /></div>
    <div><div className="text-[14px] font-medium text-navy">{label}</div>
      <div className={`font-serif-d text-[32px] font-bold leading-tight ${tone === "red" ? "text-[#d1453b]" : "text-navy"}`}>{value}</div>
      <div className="text-[13px] text-[#6b7a8c]">{sub}</div></div>
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const { setBookId } = useBook();
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  const open = (id, path = "/kitaplar/" + id) => { setBookId(id); nav(path); };

  return (
    <Page>
      <Header title="Ana Kontrol Merkezi" sub="Kitap projelerinizi yönetin, üretim süreçlerini takip edin ve hayallerinizi okurlarla buluşturun.">
        <Link to="/kitaplar/yeni" className="btn-teal px-7 py-3.5 text-lg flex items-center gap-3 font-serif-d" data-testid="new-book-btn"><Plus size={20} /> Yeni kitap oluştur</Link>
      </Header>
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_390px] gap-5">
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={BookOpen} label="Kitaplarım" value={d?.stats.books ?? "–"} sub={`${d?.stats.ready ?? 0} yayınlanmaya hazır`} testid="stat-books" />
            <Stat icon={FileText} label="Sayfalar" value={d?.stats.pages ?? "–"} sub="toplam sayfa" testid="stat-pages" />
            <Stat icon={CircleCheck} label="Onay bekleyen" value={d?.stats.pending ?? "–"} sub="sayfa ve içerik" tone="red" testid="stat-pending" />
            <Stat icon={Settings} label="Üretim işleri" value={d?.stats.jobs ?? "–"} sub="kuyrukta" testid="stat-jobs" />
          </div>
          <Card className="keep-serif" title="Son çalışılan kitaplarım" action={<Link to="/kitaplar" className="text-teal text-sm flex items-center gap-1">Tüm kitaplarım <ArrowRight size={14} /></Link>}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {d?.books.slice(0, 3).map((b) => (
                <div key={b.id} className="card-z p-2 group cursor-pointer hover:shadow-lg transition-shadow" onClick={() => open(b.id)} data-testid={`recent-book-${b.id}`}>
                  <div className="relative overflow-hidden rounded-lg">
                    <Img src={b.cover} className="w-full aspect-[5/6] group-hover:scale-[1.03] transition-transform duration-500" />
                    <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black/45 to-transparent pointer-events-none" />
                    <span className="absolute inset-x-0 top-10 text-center font-playfair text-white text-[27px] leading-[1.15] px-5 [text-shadow:0_2px_10px_rgba(0,0,0,.55)]" data-testid={`recent-book-title-${b.id}`}>{b.title}</span>
                    <Badge s={b.status} className="absolute top-3 right-3 bg-white/90">{b.status}</Badge>
                  </div>
                  <div className="px-2 pt-3 pb-1">
                    <div className="font-serif-d font-semibold text-navy">{b.title}</div>
                    <div className="text-[13px] text-[#6b7a8c]">{b.type} • {b.age_group}</div>
                    <div className="flex items-center gap-3 mt-3"><Progress v={(b.approved / (b.pages || 1)) * 100} className="flex-1" /><span className="text-xs text-[#6b7a8c]">{b.approved} / {b.pages} sayfa</span></div>
                    <div className="flex items-center justify-between mt-3 text-xs">
                      <span className="text-[#d1453b] flex items-center gap-1"><MessageSquare size={13} /> {b.pending} onay bekliyor</span>
                      <MoreHorizontal size={16} className="text-[#5f6f84]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
        <div className="space-y-5">
          <Card className="keep-serif" title="Üretim kuyruğu" action={<Link to="/varliklar" className="text-teal text-sm flex items-center gap-1" data-testid="queue-see-all">Tümünü gör <ArrowRight size={14} /></Link>} bodyClass="px-5 pb-4">
            {d?.jobs.length === 0 && <p className="text-sm text-[#8a96a8] py-3">Kuyrukta iş yok.</p>}
            {d?.jobs.slice(0, 4).map((j) => (
              <div key={j.id} className="flex gap-3 py-3 border-t first:border-0" data-testid={`queue-item-${j.id}`}>
                <Img src={j.cover} className="w-16 h-16 rounded-md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2"><div className="font-serif-d text-navy truncate">{j.book_title}</div><Badge s={j.status} /></div>
                  <div className="text-xs text-[#6b7a8c]">{j.title}</div>
                  <div className="flex items-center gap-2 mt-2"><Progress v={j.progress} className="flex-1" /><span className="text-xs text-[#6b7a8c]">%{j.progress}</span></div>
                </div>
              </div>
            ))}
          </Card>
          <Card className="keep-serif" title="Son aktiviteler" bodyClass="px-5 pb-4">
            {d?.activities.map((a) => (
              <div key={a.id} className="flex gap-3 py-3 border-t first:border-0 text-sm">
                <span className="w-10 h-10 rounded-full bg-[#e3f1f5] text-navy grid place-items-center text-xs font-semibold shrink-0">{a.initials}</span>
                <div className="flex-1 min-w-0"><div className="text-navy">{a.text}</div>
                  <div className="flex justify-between text-xs text-[#6b7a8c] mt-0.5"><span className="flex items-center gap-1"><Clock size={11} />{timeAgo(a.created_at)}</span><span className="truncate ml-2">{a.book_title}</span></div></div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </Page>
  );
}
